import { Box, CardContent, Grid, Tooltip, Typography } from "@mui/material";
import {
  assetDefaultimage,
  ErrorStatusIcon,
  ImageIcon,
  ProcessingStatusIcon,
  ReadyStatusIcon,
  SelectionTick,
  VODIcon,
} from "@platformx/shared/static-assets";
import { PlatFormXDateTimeFormat } from "@platformx/utilities";
import { memo, useState } from "react";
import { formThumbnailFromGCBURL } from "../../../utils/cdnFlow";
import "./ContentTypeCard.css";
import useContentGlleryStyle from "./DamContentGllery.style";

const ContentTypeCardNew = ({
  content,
  selectedCardIndex,
  onSelectCard,
  setImageData,
  filterVal,
  assetType,
}) => {
  const [isHover, setHovered] = useState(false);
  const [error, setError] = useState(false);

  const classes = useContentGlleryStyle();

  const isVideo = assetType === "Video" || content?.entityType === "Video";

  const playbackStatus = isVideo ? (content?.playbackStatus ?? "NOT_PLAYABLE") : null;

  const getDisplayLabel = (status: string | null) => {
    if (status === "PLAYABLE") return "Ready";
    if (status === "PROCESSING") return "Processing";
    if (status === "FAILED") return "Failed";
    return "Not Ready";
  };

  const playbackConfig: Record<string, { iconSrc: string; color: string }> = {
    PLAYABLE: {
      iconSrc: ReadyStatusIcon,
      color: "#1A1A1A",
    },
    PROCESSING: {
      iconSrc: ProcessingStatusIcon,
      color: "#1A1A1A",
    },
    FAILED: {
      iconSrc: ErrorStatusIcon,
      color: "#1A1A1A",
    },
    NOT_PLAYABLE: {
      iconSrc: ErrorStatusIcon,
      color: "#1A1A1A",
    },
  };

  const currentStatusConfig = playbackStatus
    ? (playbackConfig[playbackStatus] ?? playbackConfig.NOT_PLAYABLE)
    : null;

  const icon = {
    Image: ImageIcon,
    Video: VODIcon,
  };

  const handleClick = () => {
    setImageData({
      Thumbnail: content.thumbnail.content_url,
      Title: content?.name,
      Description: content?.description || "",
      Author: content?.thumbnail?.publisher,
      bitStreamId: content?.uuid,
      ext: content?.originalPath?.ext || "",
      bundlesUrl: content?.bitstreams?.url,
      original_image_relative_path: content?.originalPath?.original_image_relative_path || "",
      playbackStatus: content?.playbackStatus ?? "NOT_PLAYABLE",
      videoId: content?.videoId || "",
      // Brightcove's duration in ms, for VOD JSON-LD. The DAM asset payload does
      // not carry it until KIWI-6410 maps dc.video.duration, so it stays "".
      duration: content?.duration || "",
    });
    onSelectCard(content.uuid); // Assuming content.uuid is a unique identifier for the card
  };

  const gcbPath = content?.originalPath?.original_image_relative_path;
  const gcbExt = content?.originalPath?.ext;
  const cdnUrl = gcbPath ? formThumbnailFromGCBURL(gcbPath, gcbExt, "quality=85,format=webp") : "";

  const imageHeight = content?.thumbnail?.height;
  // const imageWidth = content?.thumbnail?.width;
  // type AspectRatioResult = { width: number; height: number; aspectRatio: number };

  // function getAspectRatio(): Promise<AspectRatioResult> {
  //   return new Promise((resolve, reject) => {
  //     const img = new Image();
  //     img.crossOrigin = "anonymous";
  //     img.src = webpUrl;

  //     img.onload = () => {
  //       const width = img.naturalWidth;
  //       const height = img.naturalHeight;
  //       const aspectRatio = width / height;
  //       resolve({ width: width, height: height, aspectRatio: aspectRatio });
  //     };

  //     img.onerror = () => {
  //       reject(new Error("Failed to load image for aspect ratio calculation"));
  //     };
  //   });
  // }

  // const shouldApplyObjectFit = async () => {
  //   try {
  //     const { width = 0, height = 0 } = await getAspectRatio();
  //     setApplyObjectFit(width > 290 || height > 250);
  //   } catch (error) {
  //     console.error("Failed to load image for aspect ratio check:", error);
  //     setApplyObjectFit(false); // Fallback to default
  //   }
  // };

  return (
    <Grid
      container
      item
      key={content?.uuid}
      xs={12}
      sm={6}
      md={6}
      em={4}
      lg={filterVal === "default" ? 3 : 4}
      sx={{ float: "left", position: "relative" }}>
      <Box
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-testid='select-content-type-card'
        className={`${selectedCardIndex === content.uuid ? "selected" : ""} ${
          classes.folderaddstep
        }`}>
        {/* Playback status badge for videos — icon + label pill */}
        {isVideo && currentStatusConfig && (
          <Tooltip title={getDisplayLabel(playbackStatus)}>
            <Box
              sx={{
                position: "absolute",
                top: 14,
                left: 8,
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#FFFFFF",
                borderRadius: "6px",
                padding: "4px 10px",
                boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
              }}>
              <img
                src={currentStatusConfig.iconSrc}
                alt={playbackStatus}
                style={{ width: 16, height: 16, display: "block" }}
              />
              <Typography
                variant='p4regular'
                sx={{ color: currentStatusConfig.color, lineHeight: 1, fontWeight: 500 }}>
                {getDisplayLabel(playbackStatus)}
              </Typography>
            </Box>
          </Tooltip>
        )}
        <Box className={classes.folderlistingstep}>
          {/* <Box className={classes.wrapperoverlay}></Box> */}
          {error ? (
            <img
              src={assetDefaultimage}
              style={{ objectFit: "cover", width: "inherit", height: "100%" }}
              alt='fallbackimage'
            />
          ) : (
            <picture style={imageHeight > 300 ? { height: "100%" } : {}}>
              <source srcSet={cdnUrl} type='image/avif' className={classes.container} />
              <img
                src={cdnUrl}
                alt='image-dspace'
                className={classes.container}
                style={{
                  height: "100%",
                }}
                onError={() => {
                  // (event.target as HTMLImageElement).src = assetDefaultimage;
                  setError(true);
                }}
              />
            </picture>
          )}
        </Box>

        {selectedCardIndex === content.uuid && <Box className={classes.cardmedia}></Box>}
        {isHover && <Box className={classes.cardmediaHover}></Box>}

        <CardContent className={classes.cardcontent}>
          {selectedCardIndex === content.uuid && (
            <img src={SelectionTick} className={classes.tickicon} alt='icon' />
          )}
          {(isHover || selectedCardIndex === content.uuid) && (
            <Box className={classes.contentcardwrap}>
              <Box className={classes.contentboxwrap}>
                <img
                  style={{ filter: "brightness(100)" }}
                  alt={content?.entityType}
                  src={icon[content?.entityType || "Image"]}
                />
              </Box>

              <Typography
                className={classes.contentName}
                color='textColor'
                variant='p3medium'
                sx={{ marginTop: "5px !important", marginBottom: "5px !important" }}>
                {content?.name}
              </Typography>
              <Box className={classes.boxlastcontent}>
                <Typography color='textColor' variant='p4regular'>
                  {PlatFormXDateTimeFormat(content?.lastModified)}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Box>
    </Grid>
  );
};

export default memo(ContentTypeCardNew);
